import { useState, useEffect } from "react";
import { Users, Trophy, Clock, UserPlus } from "lucide-react";
import { adminApi, ReferralOverview } from "../lib/api";

export default function Referrals() {
  const [data, setData] = useState<ReferralOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .referrals()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Referrals</h1>
        <div className="flex justify-center py-20">
          <div className="size-8 border-3 border-[#fa8585]/30 border-t-[#fa8585] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Referrals</h1>
        <p className="text-slate-500">Failed to load referral data.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Referrals</h1>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="size-10 rounded-xl bg-[#fa8585]/10 flex items-center justify-center">
              <UserPlus size={20} className="text-[#fa8585]" />
            </div>
            <span className="text-sm font-medium text-slate-500">
              Total Referrals
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {data.totalReferrals}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="size-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Trophy size={20} className="text-amber-500" />
            </div>
            <span className="text-sm font-medium text-slate-500">
              Top Referrer
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {data.topReferrers[0]?.count ?? 0}
          </p>
          <p className="text-xs text-slate-400 mt-1 truncate">
            {data.topReferrers[0]?.name ?? "—"}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="size-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-blue-500" />
            </div>
            <span className="text-sm font-medium text-slate-500">
              Unique Referrers
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {data.topReferrers.length}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Top Referrers */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" />
            Top Referrers
          </h2>
          {data.topReferrers.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">
              No referrals yet
            </p>
          ) : (
            <div className="space-y-3">
              {data.topReferrers.map((r, i) => (
                <div
                  key={r.userId}
                  className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-slate-50"
                >
                  <span className="text-sm font-bold text-slate-400 w-6">
                    {i + 1}.
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate text-sm">
                      {r.name}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{r.email}</p>
                  </div>
                  <span className="text-sm font-bold text-[#fa8585] bg-[#fa8585]/10 px-3 py-1 rounded-full">
                    {r.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Referrals */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Clock size={18} className="text-blue-500" />
            Recent Referrals
          </h2>
          {data.recentReferrals.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">
              No referrals yet
            </p>
          ) : (
            <div className="space-y-3">
              {data.recentReferrals.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-slate-50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900">
                      <span className="font-semibold">{r.referrerName}</span>
                      <span className="text-slate-400"> invited </span>
                      <span className="font-semibold">{r.referredName}</span>
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
